import { LDateTime } from '@app/components/date/LDateTime';
import { auditReports } from '@mi-front/api-clients';
import { toDateTimeStr } from '@mi-front/core';
import { MRT_ColumnDef } from 'material-react-table';
import { IntlFormatters } from 'react-intl';
import { Link } from 'react-router-dom';
import { LDate } from '@app/components/date/LDate';
import { aodRoutes } from '../../routing/routes';
import { AodReportType } from '../../components/AodReportTypeChip';
import { AodRunActions } from '../../components/AodRunActions';
import { AodRunStatus } from '../../components/AodRunStatus';
import { AodTriggerType } from '../../components/AodTriggerType';

export interface PastRunsColumnsOptions {
  includeRuleName?: boolean;
  includeReportType?: boolean;
  includeReportLink?: boolean;
  includeType?: boolean;
}

/**
 * Creates column definitions for Past Runs table V2.
 * Same shape as shared PastRunsColumns but without column-filter props
 * (filtering is done via the ListView filter bar).
 */
export const createPastRunsColumns = (
  $t: IntlFormatters['$t'],
  options: PastRunsColumnsOptions = {},
): MRT_ColumnDef<auditReports.AodRunInGetManyDto>[] => {
  const {
    includeRuleName = false,
    includeReportType = false,
    includeReportLink = false,
    includeType = true,
  } = options;

  const columns: MRT_ColumnDef<auditReports.AodRunInGetManyDto>[] = [
    {
      id: 'datetime',
      header: $t({
        id: 'audit.aod.runs.datetime',
        defaultMessage: 'Ejecutada',
      }),
      accessorFn: (row) => row.scheduledDatetime,
      Cell: ({ row: { original } }) => (
        <LDateTime value={toDateTimeStr(original.scheduledDatetime!)} showRelative />
      ),
      size: 150,
      enableSorting: true,
    },
  ];

  if (includeRuleName) {
    columns.push({
      id: 'rule.name',
      header: $t({
        id: 'audit.aod.runs.ruleName',
        defaultMessage: 'Regla',
      }),
      accessorFn: (row) => row.rule.name,
      enableSorting: true,
      Cell: ({ row: { original } }) => (
        <Link to={aodRoutes.ruleView.gen(original.rule.id)}>{original.rule.name}</Link>
      ),
      size: 100,
    });
  }

  if (includeReportType) {
    columns.push({
      id: 'rule.reportType',
      header: $t({
        id: 'audit.aod.runs.reportType',
        defaultMessage: 'Tipo de reporte',
      }),
      accessorFn: (row) => row.rule.reportType,
      Cell: ({ row: { original } }) => <AodReportType reportType={original.rule.reportType} />,
      size: 120,
    });
  }

  if (includeType) {
    columns.push({
      id: 'type',
      header: $t({
        id: 'audit.aod.runs.type',
        defaultMessage: 'Trigger',
      }),
      enableSorting: true,
      accessorFn: (row) => row.type,
      Cell: ({ row: { original } }) => <AodTriggerType type={original.type} size="small" />,
      size: 100,
    });
  }

  columns.push({
    id: 'order',
    header: $t({
      id: 'audit.aod.runs.order',
      defaultMessage: 'Orden',
    }),
    enableSorting: true,
    accessorFn: (row) => row.order,
    Cell: ({ row: { original } }) => original.order,
    size: 100,
  });

  columns.push({
    id: 'interval',
    header: $t({
      id: 'audit.aod.runs.interval',
      defaultMessage: 'Intervalo',
    }),
    accessorFn: (row) => row.interval,
    Cell: ({ row: { original } }) => {
      if (!original.interval) return '';

      const dates = original.interval.split('/');
      if (dates.length !== 2) return original.interval;

      return (
        <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
          <LDate value={toDateTimeStr(dates[0])} day="2-digit" month="2-digit" />
          <span>-</span>
          <LDate value={toDateTimeStr(dates[1])} day="2-digit" month="2-digit" />
        </div>
      );
    },
    size: 120,
  });

  columns.push({
    id: 'status',
    header: $t({
      id: 'audit.aod.runs.status',
      defaultMessage: 'Estado',
    }),
    accessorFn: (row) => row.status,
    Cell: ({ row: { original } }) => <AodRunStatus run={original} size="small" />,
    size: 100,
  });

  if (includeReportLink) {
    columns.push({
      id: 'actions',
      header: $t({
        id: 'common.text.actions',
        defaultMessage: 'Acciones',
      }),
      accessorFn: (row) => row.reportId,
      Cell: ({ row: { original } }) => <AodRunActions run={original as auditReports.AodRun} />,
      size: 120,
    });
  }

  return columns;
};
